/**
 *
 */

// pure
import { latLonToEN } from '@openlayers/ol-utils';
import { Events } from '@shared/Events';
import { DrawInteractionTool } from '@openlayers/tools/tool-base';
import { MapWrapper } from '@openlayers/MapWrapper';
// ol
import Feature from 'ol/Feature';
import { Circle as OlCircle, LineString } from 'ol/geom';
import { transform as olTransform } from 'ol/proj';
import OlOverlay from 'ol/Overlay';
import { Type as OlGeometryType } from 'ol/geom/Geometry';
import * as olStyle from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';


const FEATURE_TYPE = 'freeLongSection';

export class FreeLongSectionTool extends DrawInteractionTool {
    geometryType: OlGeometryType = 'LineString';

    features: Feature[] = [];
    alignmentUpdateOngoing = false;
    startStation = 0;
    endStation = 0;
    alignmentData: IAlignment = null;
    activeAlignmentStations: any[] = [];
    startStationOverlay: OlOverlay = null;
    endStationOverlay: OlOverlay = null;

    extraStyles = {
        wideLine: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [255, 128, 0, 0.5],
                width: 18,
            }),
            fill: new olStyle.Fill({
                color: [255, 128, 0, 0.5],
            }),
        }),
        narrow: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [0, 0, 0, 1],
                width: 1,
            }),
            fill: new olStyle.Fill({
                color: [0, 0, 0, 1],
            }),
        }),
    };

    inProgressStyle = new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: 'rgba(0, 0, 0, 0)',
            width: 2,
        }),
    });

    finishedStyle = new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: 'rgba(0, 0, 0, 0)',
            width: 2,
        }),
    });

    extrasLayer: VectorLayer<any>;

    constructor(mapWrapper: MapWrapper, private onOpen: (station1: number, station2: number) => void) {
        super(mapWrapper);
        Events.on('alignment_stations_feature_factory_changed', (e, data) => {
            this.updateAlignmentData(data.alignment, data.activeAlignmentStations);
        });
    }

    startDrawing() {
        // if we start drawing right away user needs to do 1 click less
        (this.drawInteraction as any).startDrawing_(this.mapWrapper.getCenter());
    }

    onDrawStart(e) {
        // OlDrawEvent
        const currFeature: Feature = e.feature;
        const sourceProj = this.mapWrapper.getProjection();
        this.clearLabels();

        const coord1: Coordinate = (currFeature.getGeometry() as LineString).getCoordinates()[0];
        this.startStation = this.findClosestStation(sourceProj, coord1);
        let start = this.startStation - this.alignmentData.startStation;
        if (start < 0) {
            start = 0;
        }
        this.drawStartIndicator(this.activeAlignmentStations[start]);

        this.geometryListener = currFeature.getGeometry().on('change', e => {
            if (this.alignmentUpdateOngoing) {
                return;
            }
            const geom = e.target;
            const coords = geom.getCoordinates();
            if (coords.length == 2) {
                this.startStation = this.findClosestStation(sourceProj, coords[1]);
                start = this.startStation - this.alignmentData.startStation;
                if (start < 0) {
                    start = 0;
                }
                this.drawStartIndicator(this.activeAlignmentStations[start]);
                return;
            }
            this.endStation = this.findClosestStation(sourceProj, coords[2]);
            if (coords.length == 3) {
                let end = this.endStation - this.alignmentData.startStation;
                if (end < 0) {
                    end = 0;
                }
                this.drawSelection(start, end);
            }
            if (coords.length > 3) {
                this.openSectionWindow();
            }
        });
    }

    drawSelection(startIndex: number, endIndex: number) {
        if (startIndex != endIndex) {
            this.features.forEach(f => this.extrasLayer.getSource().removeFeature(f));
            this.features = [];

            if (!this.endStationOverlay) {
                this.endStationOverlay = this.createLabel(null);
                this.mapWrapper.getInstance().addOverlay(this.endStationOverlay);
            }
            this.endStationOverlay.getElement().innerHTML = kuura.getMessage('tablet.station_js') + ': ' + this.endStation;
            this.endStationOverlay.setPosition(this.activeAlignmentStations[endIndex]);
            this.endStationOverlay.setOffset([0, -10]);
        }

        const start = startIndex <= endIndex ? startIndex : endIndex;
        const end = startIndex <= endIndex ? endIndex : startIndex;

        for (let i = start; i < end; i++) {
            const lineString: [number, number][] = [];
            lineString.push(this.activeAlignmentStations[i]);
            lineString.push(this.activeAlignmentStations[i + 1]);

            const feature = new Feature({
                geometry: new LineString(lineString),
            });
            feature.setStyle(this.extraStyles.wideLine);
            feature.set('type', FEATURE_TYPE);
            this.extrasLayer.getSource().addFeature(feature);
            this.features.push(feature);

            if (i === start || i === end - 1) {
                const index = i === start ? i : i + 1;
                const point = new Feature({
                    geometry: new OlCircle(this.activeAlignmentStations[index], 0.5, 'XY'),
                });
                point.setStyle(this.extraStyles.narrow);
                point.set('type', FEATURE_TYPE);
                this.extrasLayer.getSource().addFeature(point);
                this.features.push(point);
            }
        }
    }

    clearLabels = () => {
        if (this.startStationOverlay) {
            this.mapWrapper.getInstance().removeOverlay(this.startStationOverlay);
        }
        if (this.endStationOverlay) {
            this.mapWrapper.getInstance().removeOverlay(this.endStationOverlay);
        }
        this.startStationOverlay = null;
        this.endStationOverlay = null;
    };

    findClosestStation(sourceProj: any, mouseCoords: any) {
        const everyNmeters = 1;
        let closestStation = 0;
        const startStation = Math.round(this.alignmentData.startStation);
        const firstTickM = Math.ceil(startStation / everyNmeters) * everyNmeters;
        let smallestDistance = 99999999999;
        const mouseLonLat: Coordinate = olTransform(mouseCoords, sourceProj, 'EPSG:4326');
        const mouseEN = latLonToEN(mouseLonLat[1], mouseLonLat[0]);

        let currStationM = firstTickM - everyNmeters;
        while (currStationM < this.alignmentData.endStation + everyNmeters) {
            const stationArrayIndex = utils.clamp(0, currStationM - startStation, this.activeAlignmentStations.length - 1);
            const stationEN = this.activeAlignmentStations[stationArrayIndex]; //latLonToEN(activeAlignmentStations[stationArrayIndex].lat, activeAlignmentStations[stationArrayIndex].lon);
            const dis = this.distance2D(mouseEN, stationEN);
            if (dis < smallestDistance) {
                smallestDistance = dis;
                closestStation = currStationM;
            }
            const meters = startStation + stationArrayIndex;
            currStationM += 1;
        }
        if (closestStation < 0) {
            return 0;
        }
        if (closestStation > this.alignmentData.endStation) {
            return Math.round(this.alignmentData.endStation);
        }
        return closestStation;
    }

    distance2D(coord1: any, coord2: any) {
        const n = coord1[0] - coord2[0];
        const e = coord1[1] - coord2[1];
        return Math.sqrt(n * n + e * e);
    }

    openSectionWindow = () => {
        this.drawInteraction.finishDrawing();
        let start = this.startStation;
        let end = this.endStation;
        if (this.startStation > this.endStation) {
            start = this.endStation;
            end = this.startStation;
        }
        this.onOpen(start, end);
        this.onDrawEnd();
    };

    activate() {
        super.activate();
        this.extrasLayer = new VectorLayer({
            source: new VectorSource(),
        });
        this.mapWrapper.addLayer(this.extrasLayer, 91, 'long-section-extras');
    }

    deactivate() {
        super.deactivate();
        this.clear();
        this.mapWrapper.removeLayer(this.extrasLayer);
    }

    clear() {
        super.clear();
        this.features.forEach(f => this.extrasLayer.getSource().removeFeature(f));
        this.features = [];
        this.clearLabels();
    }

    drawStartIndicator(stationCoords: any) {
        this.clear();
        if (!stationCoords) {
            console.warn('Station coords invalid, coords: ', stationCoords);
            return;
        }
        const circle = new Feature({
            geometry: new OlCircle(stationCoords, 1.5, 'XY'),
        });
        circle.setId('free_longsection_start_indicator1');
        circle.setStyle(this.extraStyles.wideLine);
        this.extrasLayer.getSource().addFeature(circle);
        this.features.push(circle);

        const point = new Feature({
            geometry: new OlCircle(stationCoords, 0.5, 'XY'),
        });
        point.setId('free_longsection_start_indicator2');
        point.setStyle(this.extraStyles.narrow);
        this.extrasLayer.getSource().addFeature(point);
        this.features.push(point);

        this.startStationOverlay = this.createLabel(point);
        this.startStationOverlay.getElement().innerHTML = kuura.getMessage('tablet.station_js') + ': ' + this.startStation;
        this.startStationOverlay.setPosition(stationCoords);
        this.startStationOverlay.setOffset([0, -10]);
        this.mapWrapper.getInstance().addOverlay(this.startStationOverlay);
    }

    updateAlignmentData(alignment: IAlignment, stations: any) {
        this.alignmentUpdateOngoing = true;
        let alignmentChanged = false;
        if (this.alignmentData != null && alignment.modelId != this.alignmentData.modelId) {
            alignmentChanged = true;
        }
        if (alignmentChanged) {
            this.clear();
            if (this.drawInteraction) {
                this.drawInteraction.finishDrawing();
            }
            this.onDrawEnd();
        }
        this.alignmentData = alignment;
        this.alignmentData.startStation = Math.ceil(this.alignmentData.startStation);
        this.activeAlignmentStations = [];

        for (let i = 0; i < stations.length; i++) {
            this.activeAlignmentStations.push(latLonToEN(stations[i].lat, stations[i].lon));
        }
        this.alignmentUpdateOngoing = false;
        if (alignmentChanged && this.drawInteraction) {
            this.startDrawing();
        }
    }
}
